
import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api.ts";
import placeholder from "../assets/Car Image.png";
import { useLanguage } from "../context/LanguageContext";
import translations from "../locals/translations.json";
import BrandCard from "./BrandCard";

const BrandSection = () => {
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);

  const containerRef = useRef(null);

  // Responsive itemsToShow
  const computeItemsToShow = () =>
    window.innerWidth < 640 ? 2 : window.innerWidth < 1024 ? 4 : 5;
  const [itemsToShow, setItemsToShow] = useState(computeItemsToShow());

  const { language } = useLanguage();
  const isRTL = language === "ar";

  // Fetch brands
  useEffect(() => {
    const fetchPremiumBrands = async () => {  
      try {
        const response = await api.get(
          `/makes?isPremium=1`
        );
        const brandData = response.data.map((brand) => ({
          id: brand.id,
          name: brand.name,
          imageUrl: brand.imageUrl || placeholder,
          vehicleCount: brand.totalVehicles || "-",
        }));
        setBrands(brandData);
      } catch (e) {
        console.error("Error fetching brands:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchPremiumBrands();
  }, []);

  // Update itemsToShow & card width on resize
  useEffect(() => {
    const handleResize = () => {
      setItemsToShow(computeItemsToShow());
      setCurrentIndex(0);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate card width whenever container or itemsToShow changes
  useEffect(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const gap = 16; // gap-4
      const w = (containerWidth - gap * (itemsToShow - 1)) / itemsToShow;
      setCardWidth(w);
    }
  }, [itemsToShow, brands]);

  const scrollLeft = () => {
    if (currentIndex > 0) setCurrentIndex((p) => p - 1);
  };
  const scrollRight = () => {
    if (currentIndex < brands.length - itemsToShow)
      setCurrentIndex((p) => p + 1);
  };

  // Hide entire section when finished loading and no data
  if (!loading && brands.length === 0) return null;

  return (
    <section className='max-w-7xl mx-auto py-10 relative'>
      {loading && brands.length === 0 && (
        <div className='text-center py-10 text-gray-500'>
          {translations[language]?.common?.loading || "Loading..."}
        </div>
      )}

      {brands.length > 0 && (
        <>
          <div dir={isRTL ? "rtl" : "ltr"} className='relative'>
            <div className='text-center max-w-2xl mx-auto px-4'>
              <h2 className='text-[20px] md:text-[30px] font-semibold mb-2'>
                {translations[language].premiumBrands.exploreOur}{" "}
                <span className='text-primary'>
                  {translations[language].premiumBrands.premiumBrands}
                </span>
              </h2>
              <p className='text-[16px] mb-8'>
                {translations[language].premiumBrands.description}
              </p>

              {brands.length > itemsToShow && (
                <>
                  {/* Large screens: top buttons, left for RTL, right for LTR */}
                  <div
                    className={`hidden sm:absolute sm:bottom-0 sm:flex sm:space-x-2 ${
                      isRTL ? "sm:left-0" : "sm:right-0"
                    } ${isRTL ? "space-x-reverse" : ""}`}
                  >
                    <button
                      onClick={isRTL ? scrollRight : scrollLeft}
                      disabled={currentIndex === 0}
                      className={`${
                        currentIndex === 0
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:opacity-80"
                      } transition`}
                      aria-label={isRTL ? "Scroll right" : "Scroll left"}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='56'
                        height='56'
                        viewBox='0 0 56 56'
                        fill='none'
                      >
                        <g filter='url(#filter0_d_left_brand)'>
                          <path
                            d='M8 14C8 8.477 12.477 4 18 4h20c5.523 0 10 4.477 10 10v20c0 5.523-4.477 10-10 10H18C12.477 44 8 39.523 8 34V14Z'
                            fill='#414042'
                            shapeRendering='crispEdges'
                          />
                          <path
                            d='M18 4.75h20C43.109 4.75 47.25 8.891 47.25 14v20c0 5.109-4.141 9.25-9.25 9.25H18c-5.109 0-9.25-4.141-9.25-9.25V14C8.75 8.891 12.891 4.75 18 4.75Z'
                            stroke='#888888'
                            strokeOpacity='0.6'
                            strokeWidth='1.5'
                            shapeRendering='crispEdges'
                          />
                          <path
                            d={
                              isRTL
                                ? "M32.6337 24.8L25.8837 31.68C25.6744 31.8933 25.4215 32 25.125 32C24.8285 32 24.5669 31.8933 24.3401 31.68C24.1134 31.4667 24 31.2 24 30.88C24 30.56 24.1221 30.2933 24.3663 30.08L30.3314 24L24.3663 17.92C24.1221 17.7067 24 17.44 24 17.12C24 16.8 24.1134 16.5333 24.3401 16.32C24.5669 16.1067 24.8285 16 25.125 16C25.4215 16 25.6744 16.1067 25.8837 16.32L32.6337 23.2C32.8779 23.4133 33 23.68 33 24C33 24.32 32.8779 24.5867 32.6337 24.8Z"
                                : "M23 24C23 23.68 23.1221 23.4133 23.3663 23.2L30.1163 16.32C30.3256 16.1067 30.5785 16 30.875 16C31.1715 16 31.4331 16.1067 31.6599 16.32C31.8866 16.5333 32 16.8 32 17.12C32 17.44 31.8779 17.7067 31.6337 17.92L25.6686 24L31.6337 30.08C31.843 30.2933 31.9477 30.56 31.9477 30.88C31.9477 31.2 31.843 31.4667 31.6337 31.68C31.4244 31.8933 31.1715 32 30.875 32C30.5785 32 30.3256 31.8933 30.1163 31.68L23.3663 24.8C23.1221 24.5867 23 24.32 23 24Z"
                            }
                            fill='#ffffff'
                          />
                        </g>
                        <defs>
                          <filter
                            id='filter0_d_left_brand'
                            x='0'
                            y='0'
                            width='56'
                            height='56'
                            filterUnits='userSpaceOnUse'
                            colorInterpolationFilters='sRGB'
                          >
                            <feFlood floodOpacity='0' />
                            <feColorMatrix
                              in='SourceAlpha'
                              type='matrix'
                              values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                              result='hardAlpha'
                            />
                            <feOffset dy='4' />
                            <feGaussianBlur stdDeviation='4' />
                            <feComposite in2='hardAlpha' operator='out' />
                            <feColorMatrix
                              type='matrix'
                              values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0'
                            />
                            <feBlend
                              mode='normal'
                              in2='BackgroundImageFix'
                              result='effect1_dropShadow'
                            />
                            <feBlend
                              mode='normal'
                              in='SourceGraphic'
                              in2='effect1_dropShadow'
                              result='shape'
                            />
                          </filter>
                        </defs>
                      </svg>
                    </button>
                    <button
                      onClick={isRTL ? scrollLeft : scrollRight}
                      disabled={currentIndex >= brands.length - itemsToShow}
                      className={`transition ${
                        currentIndex >= brands.length - itemsToShow
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:opacity-80"
                      }`}
                      aria-label={isRTL ? "Scroll left" : "Scroll right"}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='56'
                        height='56'
                        viewBox='0 0 56 56'
                        fill='none'
                      >
                        <g filter='url(#filter0_d_right_brand)'>
                          <rect
                            x='8'
                            y='4'
                            width='40'
                            height='40'
                            rx='10'
                            fill='#414042'
                            shapeRendering='crispEdges'
                          />
                          <rect
                            x='8.75'
                            y='4.75'
                            width='38.5'
                            height='38.5'
                            rx='9.25'
                            stroke='#888888'
                            strokeOpacity='0.6'
                            strokeWidth='1.5'
                            shapeRendering='crispEdges'
                          />
                          <path
                            d={
                              isRTL
                                ? "M23 24C23 23.68 23.1221 23.4133 23.3663 23.2L30.1163 16.32C30.3256 16.1067 30.5785 16 30.875 16C31.1715 16 31.4331 16.1067 31.6599 16.32C31.8866 16.5333 32 16.8 32 17.12C32 17.44 31.8779 17.7067 31.6337 17.92L25.6686 24L31.6337 30.08C31.843 30.2933 31.9477 30.56 31.9477 30.88C31.9477 31.2 31.843 31.4667 31.6337 31.68C31.4244 31.8933 31.1715 32 30.875 32C30.5785 32 30.3256 31.8933 30.1163 31.68L23.3663 24.8C23.1221 24.5867 23 24.32 23 24C23 23.68 23.1221 23.4133 23.3663 23.2L30.1163 16.32C30.3256 16.1067 30.5785 16 30.875 16C31.1715 16 31.4331 16.1067 31.6599 16.32C31.8866 16.5333 32 16.8 32 17.12C32 17.44 31.8779 17.7067 31.6337 17.92L25.6686 24L31.6337 30.08C31.843 30.2933 31.9477 30.56 31.9477 30.88C31.9477 31.2 31.843 31.4667 31.6337 31.68C31.4244 31.8933 31.1715 32 30.875 32C30.5785 32 30.3256 31.8933 30.1163 31.68L23.3663 24.8C23.1221 24.5867 23 24.32 23 24Z"
                                : "M32.6337 24.8L25.8837 31.68C25.6744 31.8933 25.4215 32 25.125 32C24.8285 32 24.5669 31.8933 24.3401 31.68C24.1134 31.4667 24 31.2 24 30.88C24 30.56 24.1221 30.2933 24.3663 30.08L30.3314 24L24.3663 17.92C24.1221 17.7067 24 17.44 24 17.12C24 16.8 24.1134 16.5333 24.3401 16.32C24.5669 16.1067 24.8285 16 25.125 16C25.4215 16 25.6744 16.1067 25.8837 16.32L32.6337 23.2C32.8779 23.4133 33 23.68 33 24C33 24.32 32.8779 24.5867 32.6337 24.8Z"
                            }
                            fill='#ffffff'
                          />
                        </g>
                        <defs>
                          <filter
                            id='filter0_d_right_brand'
                            x='0'
                            y='0'
                            width='56'
                            height='56'
                            filterUnits='userSpaceOnUse'
                            colorInterpolationFilters='sRGB'
                          >
                            <feFlood floodOpacity='0' />
                            <feColorMatrix
                              in='SourceAlpha'
                              type='matrix'
                              values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                              result='hardAlpha'
                            />
                            <feOffset dy='4' />
                            <feGaussianBlur stdDeviation='4' />
                            <feComposite in2='hardAlpha' operator='out' />
                            <feColorMatrix
                              type='matrix'
                              values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0'
                            />
                            <feBlend
                              mode='normal'
                              in2='BackgroundImageFix'
                              result='effect1_dropShadow'
                            />
                            <feBlend
                              mode='normal'
                              in='SourceGraphic'
                              in2='effect1_dropShadow'
                              result='shape'
                            />
                          </filter>
                        </defs>
                      </svg>
                    </button>
                  </div>
                  {/* Small screens: buttons below text, left for RTL, center for LTR */}
                  <div
                    className={`flex sm:hidden ${
                      isRTL ? "justify-start" : "justify-center"
                    } space-x-2 ${isRTL ? "space-x-reverse" : ""} mb-4`}
                  >
                    <button
                      onClick={isRTL ? scrollRight : scrollLeft}
                      disabled={currentIndex === 0}
                      className={`${
                        currentIndex === 0
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:opacity-80"
                      } transition`}
                      aria-label={isRTL ? "Scroll right" : "Scroll left"}
                    >
                      {/* Left arrow SVG */}
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='56'
                        height='56'
                        viewBox='0 0 56 56'
                        fill='none'
                      >
                        <g filter='url(#filter0_d_left_brand)'>
                          <path
                            d='M8 14C8 8.477 12.477 4 18 4h20c5.523 0 10 4.477 10 10v20c0 5.523-4.477 10-10 10H18C12.477 44 8 39.523 8 34V14Z'
                            fill='#414042'
                            shapeRendering='crispEdges'
                          />
                          <path
                            d='M18 4.75h20C43.109 4.75 47.25 8.891 47.25 14v20c0 5.109-4.141 9.25-9.25 9.25H18c-5.109 0-9.25-4.141-9.25-9.25V14C8.75 8.891 12.891 4.75 18 4.75Z'
                            stroke='#888888'
                            strokeOpacity='0.6'
                            strokeWidth='1.5'
                            shapeRendering='crispEdges'
                          />
                          <path
                            d={
                              isRTL
                                ? "M32.6337 24.8L25.8837 31.68C25.6744 31.8933 25.4215 32 25.125 32C24.8285 32 24.5669 31.8933 24.3401 31.68C24.1134 31.4667 24 31.2 24 30.88C24 30.56 24.1221 30.2933 24.3663 30.08L30.3314 24L24.3663 17.92C24.1221 17.7067 24 17.44 24 17.12C24 16.8 24.1134 16.5333 24.3401 16.32C24.5669 16.1067 24.8285 16 25.125 16C25.4215 16 25.6744 16.1067 25.8837 16.32L32.6337 23.2C32.8779 23.4133 33 23.68 33 24C33 24.32 32.8779 24.5867 32.6337 24.8Z"
                                : "M23 24C23 23.68 23.1221 23.4133 23.3663 23.2L30.1163 16.32C30.3256 16.1067 30.5785 16 30.875 16C31.1715 16 31.4331 16.1067 31.6599 16.32C31.8866 16.5333 32 16.8 32 17.12C32 17.44 31.8779 17.7067 31.6337 17.92L25.6686 24L31.6337 30.08C31.843 30.2933 31.9477 30.56 31.9477 30.88C31.9477 31.2 31.843 31.4667 31.6337 31.68C31.4244 31.8933 31.1715 32 30.875 32C30.5785 32 30.3256 31.8933 30.1163 31.68L23.3663 24.8C23.1221 24.5867 23 24.32 23 24Z"
                            }
                            fill='#ffffff'
                          />
                        </g>
                        <defs>
                          <filter
                            id='filter0_d_left_brand'
                            x='0'
                            y='0'
                            width='56'
                            height='56'
                            filterUnits='userSpaceOnUse'
                            colorInterpolationFilters='sRGB'
                          >
                            <feFlood floodOpacity='0' />
                            <feColorMatrix
                              in='SourceAlpha'
                              type='matrix'
                              values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                              result='hardAlpha'
                            />
                            <feOffset dy='4' />
                            <feGaussianBlur stdDeviation='4' />
                            <feComposite in2='hardAlpha' operator='out' />
                            <feColorMatrix
                              type='matrix'
                              values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0'
                            />
                            <feBlend
                              mode='normal'
                              in2='BackgroundImageFix'
                              result='effect1_dropShadow'
                            />
                            <feBlend
                              mode='normal'
                              in='SourceGraphic'
                              in2='effect1_dropShadow'
                              result='shape'
                            />
                          </filter>
                        </defs>
                      </svg>
                    </button>
                    <button
                      onClick={isRTL ? scrollLeft : scrollRight}
                      disabled={currentIndex >= brands.length - itemsToShow}
                      className={`transition ${
                        currentIndex >= brands.length - itemsToShow
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:opacity-80"
                      }`}
                      aria-label={isRTL ? "Scroll left" : "Scroll right"}
                    >
                      {/* Right arrow SVG */}
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='56'
                        height='56'
                        viewBox='0 0 56 56'
                        fill='none'
                      >
                        <g filter='url(#filter0_d_right_brand)'>
                          <rect
                            x='8'
                            y='4'
                            width='40'
                            height='40'
                            rx='10'
                            fill='#414042'
                            shapeRendering='crispEdges'
                          />
                          <rect
                            x='8.75'
                            y='4.75'
                            width='38.5'
                            height='38.5'
                            rx='9.25'
                            stroke='#888888'
                            strokeOpacity='0.6'
                            strokeWidth='1.5'
                            shapeRendering='crispEdges'
                          />
                          <path
                            d={
                              isRTL
                                ? "M23 24C23 23.68 23.1221 23.4133 23.3663 23.2L30.1163 16.32C30.3256 16.1067 30.5785 16 30.875 16C31.1715 16 31.4331 16.1067 31.6599 16.32C31.8866 16.5333 32 16.8 32 17.12C32 17.44 31.8779 17.7067 31.6337 17.92L25.6686 24L31.6337 30.08C31.843 30.2933 31.9477 30.56 31.9477 30.88C31.9477 31.2 31.843 31.4667 31.6337 31.68C31.4244 31.8933 31.1715 32 30.875 32C30.5785 32 30.3256 31.8933 30.1163 31.68L23.3663 24.8C23.1221 24.5867 23 24.32 23 24C23 23.68 23.1221 23.4133 23.3663 23.2L30.1163 16.32C30.3256 16.1067 30.5785 16 30.875 16C31.1715 16 31.4331 16.1067 31.6599 16.32C31.8866 16.5333 32 16.8 32 17.12C32 17.44 31.8779 17.7067 31.6337 17.92L25.6686 24L31.6337 30.08C31.843 30.2933 31.9477 30.56 31.9477 30.88C31.9477 31.2 31.843 31.4667 31.6337 31.68C31.4244 31.8933 31.1715 32 30.875 32C30.5785 32 30.3256 31.8933 30.1163 31.68L23.3663 24.8C23.1221 24.5867 23 24.32 23 24Z"
                                : "M32.6337 24.8L25.8837 31.68C25.6744 31.8933 25.4215 32 25.125 32C24.8285 32 24.5669 31.8933 24.3401 31.68C24.1134 31.4667 24 31.2 24 30.88C24 30.56 24.1221 30.2933 24.3663 30.08L30.3314 24L24.3663 17.92C24.1221 17.7067 24 17.44 24 17.12C24 16.8 24.1134 16.5333 24.3401 16.32C24.5669 16.1067 24.8285 16 25.125 16C25.4215 16 25.6744 16.1067 25.8837 16.32L32.6337 23.2C32.8779 23.4133 33 23.68 33 24C33 24.32 32.8779 24.5867 32.6337 24.8Z"
                            }
                            fill='#ffffff'
                          />
                        </g>
                        <defs>
                          <filter
                            id='filter0_d_right_brand'
                            x='0'
                            y='0'
                            width='56'
                            height='56'
                            filterUnits='userSpaceOnUse'
                            colorInterpolationFilters='sRGB'
                          >
                            <feFlood floodOpacity='0' />
                            <feColorMatrix
                              in='SourceAlpha'
                              type='matrix'
                              values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                              result='hardAlpha'
                            />
                            <feOffset dy='4' />
                            <feGaussianBlur stdDeviation='4' />
                            <feComposite in2='hardAlpha' operator='out' />
                            <feColorMatrix
                              type='matrix'
                              values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0'
                            />
                            <feBlend
                              mode='normal'
                              in2='BackgroundImageFix'
                              result='effect1_dropShadow'
                            />
                            <feBlend
                              mode='normal'
                              in='SourceGraphic'
                              in2='effect1_dropShadow'
                              result='shape'
                            />
                          </filter>
                        </defs>
                      </svg>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className='relative mt-12 overflow-hidden bg-white rounded-md'>
            <div
              ref={containerRef}
              className='grid grid-flow-col auto-cols-fr gap-4 transition-transform duration-300 ease-in-out'
              style={{
                transform: `translateX(${isRTL ? "+" : "-"}${
                  currentIndex * (cardWidth + 16)
                }px)`,
                gridTemplateColumns: `repeat(${brands.length}, minmax(${cardWidth}px, 1fr))`,
              }}
            >
              {brands.map((brand) => (
                <div
                  key={brand.id}
                  className='brand-card'
                  style={{ width: `${cardWidth}px` }}
                >
                  <BrandCard brand={brand} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default BrandSection;
